import { GripVertical, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import type { ISceneCardHeaderProps } from '@/@types/propertySceneCard';

function formatSceneType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
}

export function SceneCardHeader({
  sceneNumber,
  sceneType,
  isDeleting,
  onDelete,
  dragHandleProps,
  compact = false,
  actionButtons,
}: ISceneCardHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between border-b bg-muted/30',
        compact ? 'px-3 py-2' : 'p-4'
      )}
    >
      <div className="flex items-center gap-3">
        <div
          {...dragHandleProps}
          role="button"
          aria-label={`Drag to reorder scene ${sceneNumber}`}
          tabIndex={0}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
        >
          <GripVertical className="h-5 w-5" />
        </div>
        {compact ? (
          <>
            <span className="font-medium">Scene {sceneNumber}:</span>
            <span className="text-muted-foreground">{formatSceneType(sceneType)}</span>
          </>
        ) : (
          <span className="font-medium">
            Scene {sceneNumber}: {formatSceneType(sceneType)}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        {actionButtons}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'text-muted-foreground hover:text-destructive',
                compact ? 'h-7 w-7' : 'h-8 w-8'
              )}
              disabled={isDeleting}
              aria-label={`Delete scene ${sceneNumber}`}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Scene {sceneNumber}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove this scene and its voiceover/video. This cannot be
                undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
